
import React, { useEffect, useState, useRef } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Contact = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      id="contact" 
      className="section relative" 
      ref={sectionRef}
      style={{
        backgroundImage: 'url(/assets/images/janis-ringli-grHfOZFM0es-unsplash.webp)',
        // backgroundImage: 'url(https://images.unsplash.com/photo-1500673922987-e212871fec22?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-developer-dark bg-opacity-75"></div>
      <div className="container-custom relative z-10">
        <div className="max-w-5xl mx-auto">
          <h2 
            className={`text-3xl md:text-4xl font-bold text-center mb-8 text-white opacity-0 ${
              isVisible ? 'animate-fade-in' : ''
            }`}
          >
            {t('contact.title')}
          </h2>
          
          <p 
            className={`text-center text-gray-200 mb-12 max-w-2xl mx-auto opacity-0 ${
              isVisible ? 'animate-fade-in animate-delay-100' : ''
            }`}
          >
            {t('contact.subtitle')}
          </p>
          
          <div 
            className={`bg-white bg-opacity-90 rounded-lg shadow-xl p-8 max-w-xl mx-auto opacity-0 ${
              isVisible ? 'animate-fade-in animate-delay-200' : ''
            }`}
          >
            <div className="space-y-8">
              <div className="flex items-center gap-4">
                <div className="bg-developer-accent p-3 rounded-full text-white">
                  <Mail size={24} />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-developer-dark mb-1">{t('contact.emailLabel')}</h4>
                  <a 
                    href={`mailto:${t('contact.emailAddress')}`} 
                    className="text-developer-accent hover:text-developer-accent-dark transition-colors"
                  >
                    {t('contact.emailAddress')}
                  </a>
                </div>
              </div>
              
              {/* <div className="flex items-center gap-4">
                <div className="bg-developer-accent p-3 rounded-full text-white">
                  <Phone size={24} />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-developer-dark mb-1">{t('contact.phoneLabel')}</h4>
                  <a 
                    href={`tel:${t('contact.phoneNumber')}`} 
                    className="text-developer-accent hover:text-developer-accent-dark transition-colors"
                  >
                    {t('contact.phoneNumber')}
                  </a>
                </div>
              </div> */}
              
              <div className="flex items-center gap-4">
                <div className="bg-developer-accent p-3 rounded-full text-white">
                  <MapPin size={24} />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-developer-dark mb-1">{t('contact.locationLabel')}</h4>
                  <p className="text-developer-neutral">
                    {t('contact.location')}
                  </p>
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-lg font-semibold text-developer-dark mb-3">{t('contact.connectOnline')}</h4>
                <div className="flex gap-4">
                  <a 
                    href="https://fr.linkedin.com/in/godfried-moudondi-5b182b48" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-developer-accent hover:text-developer-accent-dark transition-colors"
                  >
                    LinkedIn
                  </a>
                  <span className="text-gray-300">•</span>
                  <a 
                    href="https://github.com/idready" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-developer-accent hover:text-developer-accent-dark transition-colors"
                  >
                    GitHub
                  </a>
                  <span className="text-gray-300">•</span>
                  <a 
                    href="https://x.com/i_dready" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-developer-accent hover:text-developer-accent-dark transition-colors"
                  >
                    Twitter
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
